import { useEffect, useRef, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Button, Checkbox, ErrorState, Modal, TextField } from '../../components';
import { useSession } from '../../lib/auth/useSession';
import { today } from '../../lib/format/dates';
import { notify } from '../../services/notify';
import { localeLabel } from '../locales/api';
import { useLocalesById } from '../locales/queries';
import { SectionLabel } from '../profiles/people';
import type { Campaign } from './api';
import {
  CampaignFields,
  campaignDraftErrors,
  draftToInput,
  emptyCampaignDraft,
  type CampaignDraft,
} from './CampaignFields';
import { QrFields, emptyQrDraft, qrDraftErrors, qrDraftToInput, type QrDraft, type VariantChoice } from './QrFields';
import { useCampaignSettings, useCreateCampaign, useGenerateQR } from './queries';
import { CHANNEL_LABEL, COLOR_LABEL, TYPE_LABEL, isAppUrl, langLabel, slugSuffix } from './parts';

/** Variant values the BE will assign to descriptions given at creation: a, b, c… */
function letter(i: number): string {
  return String.fromCharCode(97 + i);
}

/**
 * New campaign — one form, two API calls. The campaign (with its variants),
 * then its first QR code when "Generate a QR code" is checked. Save opens a
 * confirmation: the key data, a reminder when there are no variants, and an
 * explicit OK for a target outside the app. If the QR code fails the campaign
 * is kept and only the QR code is retried — nothing is created twice.
 *
 * The form closes only by Cancel: a stray click outside, Escape or a close
 * button would throw the whole form away. The target URL starts as the app
 * (where nearly every campaign lands), and choosing the QR code channel turns
 * on "Generate a QR code".
 */
export function NewCampaignDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const { profile } = useSession();
  const settings = useCampaignSettings();
  const create = useCreateCampaign();
  const generate = useGenerateQR();

  const [draft, setDraft] = useState<CampaignDraft>(() => emptyCampaignDraft(slugSuffix(today(profile?.timezone))));
  const [variants, setVariants] = useState<string[]>([]);
  const [qrOn, setQrOn] = useState(false);
  const [qr, setQr] = useState<QrDraft>(() => emptyQrDraft());
  const [showErrors, setShowErrors] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [noVariantsOk, setNoVariantsOk] = useState(false);
  const [notAppOk, setNotAppOk] = useState(false);
  const [created, setCreated] = useState<Campaign | null>(null);
  const [qrFailed, setQrFailed] = useState(false);

  const choices: VariantChoice[] = variants.length
    ? variants.map((d, i) => ({ value: letter(i), label: `${letter(i).toUpperCase()}${d ? ` · ${d}` : ''}` }))
    : [{ value: '', label: 'Base link' }];
  const qrVariant = choices.some((c) => c.value === qr.variant) ? qr.variant : (choices[0]?.value ?? '');
  const qrDraft = { ...qr, variant: qrVariant };

  const fieldErrors = campaignDraftErrors(draft);
  const variantErrors = variants.some((d) => !d.trim()) ? 'Describe every variant, or remove it.' : null;
  const qrErrors = qrOn ? qrDraftErrors(qrDraft, { variants: choices }) : {};
  const valid = !Object.keys(fieldErrors).length && !variantErrors && !Object.keys(qrErrors).length;

  // Fill the target URL with the app once its address is known, unless the
  // admin already typed one.
  const appDomain = settings.data?.appDomain;
  const targetFilled = useRef(false);
  useEffect(() => {
    if (!appDomain || targetFilled.current) return;
    targetFilled.current = true;
    setDraft((d) => (d.targetUrl ? d : { ...d, targetUrl: `${appDomain.replace(/\/+$/, '')}/` }));
  }, [appDomain]);

  const onDraftChange = (next: CampaignDraft) => {
    if (next.channel === 'qrcode' && draft.channel !== 'qrcode') setQrOn(true);
    setDraft(next);
  };

  const external = !!draft.targetUrl && !isAppUrl(draft.targetUrl, settings.data?.appDomain);
  const localeName = useLocalesById(draft.localeId ? [draft.localeId] : [])[0]?.data;

  const makeQr = async (campaign: Campaign) => {
    try {
      await generate.mutateAsync(qrDraftToInput(qrDraft, campaign.id));
    } catch {
      setQrFailed(true); // shown below; the campaign stands
      return;
    }
    notify.success('Campaign and QR code created.');
    onCreated(campaign.id);
  };

  const save = async () => {
    let campaign: Campaign;
    try {
      campaign = await create.mutateAsync({
        ...draftToInput(draft),
        variants: variants.map((description) => ({ description: description.trim() })),
        targetUrlConfirmed: external ? notAppOk : undefined,
      });
    } catch {
      return; // create.error is shown in the confirmation
    }
    setCreated(campaign);
    if (!qrOn) {
      notify.success('Campaign created.');
      onCreated(campaign.id);
      return;
    }
    await makeQr(campaign);
  };

  // --- QR failed after the campaign was created: retry the QR code only ------------
  if (created && qrFailed) {
    return (
      <Modal isOpen onClose={() => onCreated(created.id)} size="md" title="Campaign created — QR code failed">
        <p className="mt-0 text-sm text-muted">
          <strong className="text-ink">{created.name}</strong> was saved. The QR code was not created.
        </p>
        {generate.error && <ErrorState error={generate.error} compact />}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost-dark" size="sm" onClick={() => onCreated(created.id)}>
            Open campaign
          </Button>
          <Button size="sm" loading={generate.isPending} onClick={() => void makeQr(created)}>
            Retry QR code
          </Button>
        </div>
      </Modal>
    );
  }

  // --- confirmation ------------------------------------------------------------------
  if (confirming) {
    const blocked = (!variants.length && !noVariantsOk) || (external && !notAppOk);
    const rows: Array<[string, string]> = [
      ['Name', draft.name],
      ['Campaign ID', draft.slug],
      ['Type', draft.type ? TYPE_LABEL[draft.type] : ''],
      ['Channel', draft.channel ? CHANNEL_LABEL[draft.channel] : ''],
      ['Locale', localeName ? localeLabel(localeName) : draft.localeId],
      ['Source', draft.utmSource],
      ['Term', draft.utmTerm || '—'],
      ['Target URL', draft.targetUrl],
      [
        'Variants',
        variants.length
          ? variants.map((d, i) => `${letter(i).toUpperCase()} · ${d}`).join('\n')
          : 'None — one base link',
      ],
      [
        'QR code',
        qrOn
          ? [
              choices.find((c) => c.value === qrVariant)?.label,
              COLOR_LABEL[qrDraft.color],
              langLabel(qrDraft.lang),
              qrDraft.connection ? 'with connection' : '',
            ]
              .filter(Boolean)
              .join(' · ')
          : 'None',
      ],
    ];
    return (
      <Modal
        isOpen
        onClose={() => setConfirming(false)}
        size="md"
        title="Create this campaign?"
        footer={
          <>
            <Button variant="ghost-dark" size="sm" onClick={() => setConfirming(false)} disabled={create.isPending}>
              Back
            </Button>
            <Button
              size="sm"
              disabled={blocked}
              loading={create.isPending || generate.isPending}
              onClick={() => void save()}
            >
              Create campaign
            </Button>
          </>
        }
      >
        <dl className="m-0 grid grid-cols-[120px_minmax(0,1fr)] gap-x-3 gap-y-2 text-sm">
          {rows.map(([k, v]) => (
            <div key={k} className="contents">
              <dt className="text-muted">{k}</dt>
              <dd className="m-0 whitespace-pre-line break-words text-ink">{v}</dd>
            </div>
          ))}
        </dl>
        {settings.data && (
          <p className="mb-0 mt-4 text-xs text-muted">
            Links and QR codes will look like <span className="font-mono">{settings.data.shortLinkBase}…</span>
          </p>
        )}
        <div className="mt-4 grid gap-2.5">
          {!variants.length && (
            <Checkbox
              checked={noVariantsOk}
              onChange={(e) => setNoVariantsOk(e.target.checked)}
              label="This campaign will not have variants (you can still add them later)."
            />
          )}
          {external && (
            <Checkbox
              checked={notAppOk}
              onChange={(e) => setNotAppOk(e.target.checked)}
              label="I know this is not a link to the app."
            />
          )}
        </div>
        {create.error && <ErrorState error={create.error} compact />}
      </Modal>
    );
  }

  // --- the form ----------------------------------------------------------------------
  const shown = showErrors ? fieldErrors : {};
  return (
    <Modal
      isOpen
      onClose={onClose}
      dismissible={false}
      size="xl"
      title="New campaign"
      footer={
        <>
          <Button variant="ghost-dark" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={() => {
              setShowErrors(true);
              if (valid) setConfirming(true);
            }}
          >
            Review
          </Button>
        </>
      }
    >
      <CampaignFields draft={draft} onChange={onDraftChange} errors={shown} />

      <div className="mt-5 border-t border-[rgba(143,68,32,.1)] pt-4">
        <Checkbox checked={qrOn} onChange={(e) => setQrOn(e.target.checked)} label="Generate a QR code" />
        {qrOn && (
          <div className="mt-3.5">
            <QrFields draft={qrDraft} onChange={setQr} variants={choices} errors={showErrors ? qrErrors : {}} />
          </div>
        )}
      </div>

      <div className="mt-5 border-t border-[rgba(143,68,32,.1)] pt-4">
        <SectionLabel>Variants</SectionLabel>
        <p className="mb-2.5 mt-0 text-xs text-muted">
          Each variant gets its own links (utm_content a, b, c…). With variants there is no plain link.
        </p>
        {variants.map((d, i) => (
          <div key={i} className="mb-2 flex items-center gap-2">
            <span className="w-7 text-center font-mono text-sm font-semibold text-brown-deep">
              {letter(i).toUpperCase()}
            </span>
            <TextField
              surface="white"
              containerClassName="flex-1"
              value={d}
              aria-label={`Variant ${letter(i).toUpperCase()} description`}
              placeholder="What makes this variant different (e.g. market stall banner)"
              onChange={(e) => setVariants((vs) => vs.map((v, j) => (j === i ? e.target.value : v)))}
            />
            <button
              type="button"
              aria-label={`Remove variant ${letter(i).toUpperCase()}`}
              onClick={() => setVariants((vs) => vs.filter((_, j) => j !== i))}
              className="flex h-8 w-8 items-center justify-center rounded-app border-0 bg-transparent text-muted hover:bg-[rgba(143,68,32,.08)]"
            >
              <X size={15} aria-hidden="true" />
            </button>
          </div>
        ))}
        {showErrors && variantErrors && <p className="m-0 text-[13px] text-danger">{variantErrors}</p>}
        <Button variant="ghost-dark" size="sm" onClick={() => setVariants((vs) => [...vs, ''])}>
          <Plus size={14} aria-hidden="true" /> Add variant
        </Button>
      </div>
    </Modal>
  );
}
